import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { congresoPublicoApi } from '../services/congresoPublicoApi';
import {
  CongresoPublico,
  AgendaSesion,
  ExpositorPublico,
  SponsorPublico,
  CongresoFaq,
  CongresoSeccionAlojamientoPublico
} from '../types/congreso.types';
import { Navbar } from '../components/Navbar';
import { HeroSection } from '../components/HeroSection';
import { ScheduleSection } from '../components/ScheduleSection';
import { LiveStreamSection } from '../components/LiveStreamSection';
import { SpeakersSection } from '../components/SpeakersSection';
import { SorteoAutoCard } from '../components/SorteoAutoCard';
import { SponsorsSection } from '../components/SponsorsSection';
import { VenueSection } from '../components/VenueSection';
import { AccommodationsSection } from '../components/AccommodationsSection';
import { FaqAccordion } from '../components/FaqAccordion';
import { Footer } from '../components/Footer';
import { formatearRangoFechas } from '../utils/dateFormatter';

export const LandingView: React.FC = () => {
  const { idCongreso } = useParams<{ idCongreso?: string }>();
  const [congreso, setCongreso] = useState<CongresoPublico | null>(null);
  const [agenda, setAgenda] = useState<AgendaSesion[]>([]);
  const [expositores, setExpositores] = useState<ExpositorPublico[]>([]);
  const [sponsors, setSponsors] = useState<SponsorPublico[]>([]);
  const [faqs, setFaqs] = useState<CongresoFaq[]>([]);
  const [alojamientosData, setAlojamientosData] = useState<CongresoSeccionAlojamientoPublico>({
    textoAlojamiento: null,
    alojamientos: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setLoading(true);
        let congresoData: CongresoPublico;

        if (idCongreso) {
          congresoData = await congresoPublicoApi.getCongresoPorId(Number(idCongreso));
        } else {
          congresoData = await congresoPublicoApi.getCongresoActivo();
        }

        setCongreso(congresoData);

        if (congresoData && congresoData.idCongreso) {
          const [agendaData, expositoresData, sponsorsData, faqsData, alojamientosRes] = await Promise.all([
            congresoPublicoApi.getAgenda(congresoData.idCongreso).catch(() => []),
            congresoPublicoApi.getExpositores(congresoData.idCongreso).catch(() => []),
            congresoPublicoApi.getSponsors(congresoData.idCongreso).catch(() => []),
            congresoPublicoApi.getFaqs(congresoData.idCongreso).catch(() => []),
            (congresoPublicoApi.getAlojamientos
              ? congresoPublicoApi.getAlojamientos(congresoData.idCongreso)
              : Promise.resolve({ textoAlojamiento: null, alojamientos: [] })
            ).catch(() => ({
              textoAlojamiento: null,
              alojamientos: []
            }))
          ]);

          setAgenda(agendaData);
          setAlojamientosData(alojamientosRes || { textoAlojamiento: null, alojamientos: [] });

          // Consolidar disertantes entre los del endpoint general y los asignados en el cronograma
          const mapaExpositores = new Map<number, ExpositorPublico>();

          (expositoresData || []).forEach(exp => {
            if (exp && exp.idExpositor) {
              mapaExpositores.set(exp.idExpositor, exp);
            }
          });

          (agendaData || []).forEach(sesion => {
            sesion.expositores?.forEach(exp => {
              if (exp && exp.idExpositor && !mapaExpositores.has(exp.idExpositor)) {
                mapaExpositores.set(exp.idExpositor, {
                  idExpositor: exp.idExpositor,
                  nombreCompleto: exp.nombreCompleto,
                  especialidad: 'Disertante',
                  cargoInstitucion: exp.cargoInstitucion || 'Disertante',
                  bio: exp.especialidad && exp.especialidad.length > 20 ? exp.especialidad : null,
                  fotoUrl: exp.fotoUrl,
                  twitterUrl: null,
                  linkedInUrl: null
                });
              }
            });
          });

          setExpositores(Array.from(mapaExpositores.values()));
          setSponsors(sponsorsData);
          setFaqs(faqsData);
        }
      } catch (err) {
        console.error('Error cargando congreso:', err);
      } finally {
        setLoading(false);
      }
    };

    cargarDatos();
  }, [idCongreso]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center text-brand-navy">
        <div className="w-16 h-16 border-4 border-brand-cyan/20 border-t-brand-cyan rounded-full animate-spin mb-4"></div>
        <p className="text-sm font-bold tracking-wider uppercase text-brand-navy">
          Cargando Congreso Inmobiliario...
        </p>
      </div>
    );
  }

  // Fallback seguro si no hay ningún congreso retornado por el API
  const congresoFinal: CongresoPublico = congreso || {
    idCongreso: 1,
    nombre: 'Congreso Inmobiliario',
    sede: 'Sede del Evento',
    domicilio: '',
    fechaDesde: new Date().toISOString(),
    fechaHasta: new Date().toISOString(),
    imagenBannerUrl: null,
    imagenLogoUrl: null,
    capacidadTotal: 0,
    asistentesConfirmados: 0,
    habilitaSorteoAuto: false,
    imagenPremioUrl: null,
    esActivo: true
  };

  const anioCongreso = congresoFinal.fechaDesde
    ? new Date(congresoFinal.fechaDesde).getFullYear()
    : 2026;
  const textoFechas = formatearRangoFechas(congresoFinal.fechaDesde, congresoFinal.fechaHasta);

  const textoAlojamiento = alojamientosData.textoAlojamiento || alojamientosData.textoIntroductorio;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar
        congreso={congresoFinal}
        hasAgenda={agenda.length > 0}
        hasSpeakers={expositores.length > 0}
        hasSponsors={sponsors.length > 0}
        hasAccommodations={Boolean(
          (textoAlojamiento && textoAlojamiento.trim()) ||
          (alojamientosData.alojamientos && alojamientosData.alojamientos.length > 0)
        )}
      />

      <main className="flex-1">
        <HeroSection congreso={congresoFinal} />
        <ScheduleSection
          agenda={agenda}
          programaPdfUrl={congresoFinal.programaPdfUrl}
          anioCongreso={anioCongreso}
        />
        <LiveStreamSection fechaRango={textoFechas} />
        <SpeakersSection expositores={expositores} />
        <SorteoAutoCard
          habilitaSorteo={congresoFinal.habilitaSorteoAuto}
          imagenPremioUrl={congresoFinal.imagenPremioUrl}
          tituloSorteo={congresoFinal.tituloSorteo}
          descripcionPremio={congresoFinal.descripcionPremio}
          idCongreso={congresoFinal.idCongreso}
        />
        <SponsorsSection sponsors={sponsors} />
        <VenueSection
          sede={congresoFinal.sede}
          domicilio={congresoFinal.domicilio}
          mapaEmbedUrl={congresoFinal.mapaEmbedUrl}
        />
        <AccommodationsSection
          textoAlojamiento={textoAlojamiento}
          textoIntroductorio={textoAlojamiento}
          alojamientos={alojamientosData.alojamientos}
          nombreCongreso={congresoFinal.nombre}
        />
        <FaqAccordion faqs={faqs} />
      </main>

      <Footer congreso={congresoFinal} />
    </div>
  );
};
